// src/components/reports/BeckAnksiyeteReports.js

import React from 'react';
import { AlertTriangle, CheckCircle, Activity, HeartPulse, ShieldAlert, ShieldCheck, Download, Loader2 } from 'lucide-react';
import EditableAIBox from '../EditableAIBox';

// --- 1. BİREYSEL RAPOR ARAYÜZÜ ---
export const BeckAnksiyeteReportUI = ({ data, meta }) => {
  const totalScore = data?.totalScore ?? 0;
  const summary = data?.summary || "NORMAL / MİNİMAL ANKSİYETE";
  const analysis = data?.analysis || "";
  const severity = data?.severity || "minimal";

  const isSevere = severity === "severe";
  const isModerate = severity === "moderate";

  return (
    <div className="space-y-6 print:space-y-4 font-sans flex flex-col">
      {/* ÜST BAŞLIK */}
      <div className={`p-5 print:p-3 rounded-2xl flex justify-between items-center shadow-sm print:bg-white print:text-black print:shadow-none print:border-b-2 print:border-slate-900 ${isSevere ? 'bg-red-900 text-white' : isModerate ? 'bg-amber-900 text-white' : 'bg-slate-900 text-white'}`}>
        <div className="flex items-center gap-4 print:gap-3">
          <div className={`h-12 w-12 print:h-9 print:w-9 rounded-xl flex items-center justify-center shadow-inner ${isSevere ? 'bg-red-600' : isModerate ? 'bg-amber-600' : 'bg-emerald-600'}`}>
            {isSevere ? <ShieldAlert className="h-6 w-6 text-white"/> : <HeartPulse className="h-6 w-6 text-white"/>}
          </div>
          <div>
            <h2 className="text-xl print:text-lg font-bold leading-none mb-1">Beck Anksiyete Ölçeği Raporu</h2>
            <span className="text-[10px] print:text-[10px] text-slate-300 print:text-slate-600 font-bold tracking-widest uppercase block">
              {meta?.studentClass || "Sınıf Belirtilmemiş"} • ÖĞRENCİ NO: {meta?.studentNo || "-"}
            </span>
          </div>
        </div>
        <div className="bg-white/10 px-4 py-2 print:px-3 print:py-1.5 rounded-lg text-center print:border print:border-slate-300">
          <span className={`text-2xl print:text-xl font-black block leading-none ${isSevere ? 'text-red-300' : isModerate ? 'text-amber-300' : 'text-emerald-400'}`}>{totalScore}</span>
          <span className="text-[9px] print:text-[9px] block opacity-70 uppercase font-bold mt-0.5">Toplam Skor / 63</span>
        </div>
      </div>

      {/* SKOR VE DERECE KARTI */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 print:gap-3">
        <div className="col-span-1 bg-slate-50 p-5 rounded-2xl border border-slate-200 flex flex-col justify-center items-center text-center print:bg-white">
          <span className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Değerlendirme Sonucu</span>
          <span className={`text-lg font-black ${isSevere ? 'text-red-600' : isModerate ? 'text-amber-600' : 'text-emerald-600'}`}>
            {summary}
          </span>
          <span className="text-xs text-slate-500 mt-2 font-medium">Toplam Puan: {totalScore} / 63</span>
        </div>

        <div className="col-span-2 bg-slate-50 p-5 rounded-2xl border border-slate-200 flex flex-col justify-center print:bg-white">
          <h3 className="font-bold text-slate-800 text-xs uppercase tracking-widest mb-2 border-b pb-1">Klinik Değerlendirme & Yorum</h3>
          <p className="text-xs text-slate-700 leading-relaxed font-medium text-justify">
            {analysis}
          </p>
        </div>
      </div>

      {/* DERECE CETVELİ */}
      <div className="bg-white p-5 rounded-2xl border border-slate-200 print:border-slate-300">
        <h3 className="font-bold text-slate-800 text-xs uppercase tracking-widest mb-3">Anksiyete Şiddet Cetveli</h3>
        <div className="grid grid-cols-4 gap-2 text-center text-[10px] font-bold">
          <div className={`p-2.5 rounded-xl border ${totalScore <= 7 ? 'bg-emerald-100 text-emerald-800 border-emerald-300 ring-2 ring-emerald-400' : 'bg-slate-50 text-slate-400 border-slate-200'}`}>
            0 - 7 Puan<br/>Minimal Anksiyete
          </div>
          <div className={`p-2.5 rounded-xl border ${totalScore >= 8 && totalScore <= 15 ? 'bg-blue-100 text-blue-800 border-blue-300 ring-2 ring-blue-400' : 'bg-slate-50 text-slate-400 border-slate-200'}`}>
            8 - 15 Puan<br/>Hafif Anksiyete
          </div>
          <div className={`p-2.5 rounded-xl border ${totalScore >= 16 && totalScore <= 25 ? 'bg-amber-100 text-amber-800 border-amber-300 ring-2 ring-amber-400' : 'bg-slate-50 text-slate-400 border-slate-200'}`}>
            16 - 25 Puan<br/>Orta Anksiyete
          </div>
          <div className={`p-2.5 rounded-xl border ${totalScore >= 26 ? 'bg-red-100 text-red-800 border-red-300 ring-2 ring-red-400' : 'bg-slate-50 text-slate-400 border-slate-200'}`}>
            26 - 63 Puan<br/>Şiddetli Anksiyete
          </div>
        </div>
      </div>
    </div>
  );
};

// --- 2. KURUMSAL (TOPLU) HESAPLAMA MOTORU ---
export const aggregateBeckAnksiyete = (subs) => {
  const total = subs.length;
  if (total === 0) return null;

  let severeCount = 0;
  let moderateCount = 0;
  let mildCount = 0;
  let minimalCount = 0;
  let sumScore = 0;

  subs.forEach(s => {
    const answers = s.answers || {};
    let subTotal = 0;
    Object.keys(answers).forEach(qId => {
      const val = Number(answers[qId]);
      if (!isNaN(val)) subTotal += val;
    });

    sumScore += subTotal;
    if (subTotal >= 26) severeCount++;
    else if (subTotal >= 16) moderateCount++;
    else if (subTotal >= 8) mildCount++;
    else minimalCount++;
  });

  const avgScore = (sumScore / total).toFixed(1);

  return {
    total,
    severeCount,
    moderateCount,
    mildCount,
    minimalCount,
    avgScore,
    severePercent: Math.round((severeCount / total) * 100),
    moderatePercent: Math.round((moderateCount / total) * 100),
    mildPercent: Math.round((mildCount / total) * 100),
    minimalPercent: Math.round((minimalCount / total) * 100)
  };
};

// --- 3. KURUMSAL RAPOR ARAYÜZÜ ---
export const BeckAnksiyeteKurumsalUI = ({ title, subs, aiResult, onGenerateAI, aiLoading, handlePrint, downloading }) => {
  const data = aggregateBeckAnksiyete(subs);
  if (!data) return <div className="p-8 text-center text-slate-500">Bu gruba ait veri bulunamadı.</div>;

  return (
    <>
      <div className="flex justify-end mb-4 no-print px-2">
        <button onClick={() => handlePrint('beck-kurumsal-area')} disabled={downloading} className="bg-blue-600 text-white px-5 py-2.5 rounded-xl font-bold flex items-center text-sm shadow-md hover:bg-blue-700 transition">
          {downloading ? <Loader2 className="animate-spin h-5 w-5 mr-2"/> : <Download className="h-5 w-5 mr-2"/>} Raporu Yazdır / PDF
        </button>
      </div>

      <div id="beck-kurumsal-area" className="font-sans flex flex-col bg-white print:bg-white p-4">
        <div className="p-5 rounded-2xl bg-slate-900 text-white flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-black">{title}</h2>
            <span className="text-xs text-slate-300 font-bold uppercase tracking-widest">BECK ANKSİYETE ÖLÇEĞİ • KURUMSAL DAĞILIM HARİTASI</span>
          </div>
          <div className="text-right">
            <span className="text-2xl font-black block">{data.total}</span>
            <span className="text-[9px] opacity-70 uppercase font-bold">Öğrenci</span>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-200 text-center">
            <span className="text-xs font-bold text-emerald-700 uppercase">Minimal (0-7)</span>
            <span className="text-2xl font-black text-emerald-800 block mt-1">%{data.minimalPercent}</span>
            <span className="text-[10px] text-emerald-600">{data.minimalCount} Öğrenci</span>
          </div>
          <div className="bg-blue-50 p-4 rounded-xl border border-blue-200 text-center">
            <span className="text-xs font-bold text-blue-700 uppercase">Hafif (8-15)</span>
            <span className="text-2xl font-black text-blue-800 block mt-1">%{data.mildPercent}</span>
            <span className="text-[10px] text-blue-600">{data.mildCount} Öğrenci</span>
          </div>
          <div className="bg-amber-50 p-4 rounded-xl border border-amber-200 text-center">
            <span className="text-xs font-bold text-amber-700 uppercase">Orta (16-25)</span>
            <span className="text-2xl font-black text-amber-800 block mt-1">%{data.moderatePercent}</span>
            <span className="text-[10px] text-amber-600">{data.moderateCount} Öğrenci</span>
          </div>
          <div className="bg-red-50 p-4 rounded-xl border border-red-200 text-center">
            <span className="text-xs font-bold text-red-700 uppercase">Şiddetli (26+)</span>
            <span className="text-2xl font-black text-red-800 block mt-1">%{data.severePercent}</span>
            <span className="text-[10px] text-red-600">{data.severeCount} Öğrenci</span>
          </div>
        </div>
      </div>
    </>
  );
};